import { isBefore } from 'date-fns'
import { cloneDeep } from 'lodash'
import React, { createContext, useReducer, useContext, ReactNode } from 'react'
import { Practitioner } from '@customTypes/Practitioner'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { GroupedAvailabilityMonth } from 'src/utils/groupAvailabilitiesByWeeks/groupAvailabilitiesByWeeks'
import { usePractitioners } from '@hooks/usePractitioners'

type SetPractitionersAction = {
  type: 'setPractitioners'
  payload: Practitioner[]
}

type ClearPractitionersAction = {
  type: 'clearPractitioners'
}

type SetPractitionersLoadingAction = {
  type: 'setPractitionersLoadingAction'
  payload: boolean
}

type SetAvailabilitiesData = {
  type: 'setAvailabilitiesData'
  payload: GroupedAvailabilityMonth[]
}

type SetSelectedPractitionersForAction = {
  type: 'setSelectedPractitionersFor'
  payload: SelectedPractitionersFor
}

type ResetSelectedPractitionersForAction = {
  type: 'resetSelectedPractitionersFor'
}

type Action =
  | SetPractitionersAction
  | ClearPractitionersAction
  | SetPractitionersLoadingAction
  | SetAvailabilitiesData
  | SetSelectedPractitionersForAction
  | ResetSelectedPractitionersForAction

interface SelectedPractitionersFor {
  treatmentReferenceId?: string
  patientType?: PatientTypeOptions
  patientReferenceId: string
}

type State = {
  allPractitioners: Practitioner[]
  isLoading: boolean
  availabilitiesData: GroupedAvailabilityMonth[]
  selectedPractitionersFor: SelectedPractitionersFor
}

type Dispatch = (action: Action) => void

const PractitionersContext = createContext<State | undefined>(undefined)
const PractitionersDispatchContext = createContext<Dispatch | undefined>(undefined)

const defaultState: State = {
  allPractitioners: [],
  isLoading: false,
  availabilitiesData: [],
  selectedPractitionersFor: {
    patientType: undefined,
    treatmentReferenceId: undefined,
    patientReferenceId: '',
  },
}

const practitionersReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'setPractitioners': {
      const newPractitioners = action.payload
      const currentPractitioners = cloneDeep(state.allPractitioners)

      newPractitioners.forEach((newPractitioner) => {
        const currentPractitioner = currentPractitioners.find(
          (currentPractitioner) => currentPractitioner.referenceId === newPractitioner.referenceId
        )
        // We're fetching practitioners in chunks for periods of time,
        // it might happen that response from the second period is returned before the first one,
        // so when we receive response from the first one we have to update availability, so always the first availability is presented
        if (currentPractitioner) {
          const currentPractitionerNextAvailability = new Date(currentPractitioner.nextAvailable || '')
          const newPractitionerNextAvailability = new Date(newPractitioner.nextAvailable || '')
          const newPractitionerHasEarlierAvailability = isBefore(
            newPractitionerNextAvailability,
            currentPractitionerNextAvailability
          )
          if (newPractitionerHasEarlierAvailability) {
            currentPractitioner.nextAvailable = newPractitioner.nextAvailable
          }
        } else {
          currentPractitioners.push(newPractitioner)
        }
      })

      return { ...state, allPractitioners: currentPractitioners }
    }
    case 'clearPractitioners': {
      return { ...state, allPractitioners: [] }
    }
    case 'setPractitionersLoadingAction':
      return { ...state, isLoading: action.payload }
    case 'setAvailabilitiesData':
      return { ...state, availabilitiesData: action.payload }
    case 'setSelectedPractitionersFor':
      return { ...state, selectedPractitionersFor: action.payload }
    case 'resetSelectedPractitionersFor':
      return {
        ...state,
        selectedPractitionersFor: { patientType: undefined, treatmentReferenceId: undefined, patientReferenceId: '' },
      }
  }
}

interface PractitionersProviderProps {
  children: ReactNode
}

export const PractitionersProvider = ({ children }: PractitionersProviderProps) => {
  const [state, dispatch] = useReducer(practitionersReducer, { ...defaultState })

  return (
    <PractitionersContext.Provider value={{ ...state }}>
      <PractitionersDispatchContext.Provider value={dispatch}>
        <ChildrenWrapper>{children}</ChildrenWrapper>
      </PractitionersDispatchContext.Provider>
    </PractitionersContext.Provider>
  )
}

interface ChildrenWrapperProps {
  children: ReactNode
}
const ChildrenWrapper = ({ children }: ChildrenWrapperProps) => {
  usePractitioners()

  return <>{children}</>
}

export const usePractitionersContext = () => {
  const context = useContext(PractitionersContext)
  if (context === undefined) {
    throw new Error('usePractitionersState must be user within a PractitionersProvider')
  }
  return context
}

export const usePractitionersDispatch = () => {
  const context = useContext(PractitionersDispatchContext)
  if (context === undefined) {
    throw new Error('usePractitionersDispatch must be used within a PractitionersProvider')
  }
  return context
}

export const clearPractitioners = (): ClearPractitionersAction => ({ type: 'clearPractitioners' })

export const setPractitionersLoading = (isLoading: boolean): SetPractitionersLoadingAction => ({
  type: 'setPractitionersLoadingAction',
  payload: isLoading,
})

export const setPractitioners = (practitioners: Practitioner[]): SetPractitionersAction => ({
  type: 'setPractitioners',
  payload: practitioners,
})

export const setAvailabilitiesData = (payload: GroupedAvailabilityMonth[]): SetAvailabilitiesData => ({
  type: 'setAvailabilitiesData',
  payload,
})

export const setSelectedPractitionersFor = (payload: SelectedPractitionersFor): SetSelectedPractitionersForAction => ({
  type: 'setSelectedPractitionersFor',
  payload,
})

export const resetSelectedPractitionersFor = (): ResetSelectedPractitionersForAction => ({
  type: 'resetSelectedPractitionersFor',
})
